import React, { useState } from 'react';
import Icon from '@components/atoms/icon/icon';
import Button from '@components/atoms/button/button';
import { useChat } from '@context/chat.context';
import VCImage from '@components/atoms/vc-images/vc-image';
import clsx from 'clsx';
import { useTranslation } from 'next-i18next';
import dynamic from 'next/dynamic';
import { CartContext } from '@context/cart.context';
import PreviewBox from '@components/atoms/preview-box/preview-box';
import logger from '@helpers/utils/logger/client';
import FloatingCart from '@components/atoms/cart/floating-cart/floating-cart';
import { useContextSelector } from 'use-context-selector';
import ConditionalWrapper from '@components/common/conditional-wrapper/conditional-wrapper';
import { useReceiverInfo } from '@hooks/chat/use-receiver-info';
import BuyerFeesModal from '@components/buyer-fees/buyer-fees-modale/buyer-fees-modale';
import useAnalyticEvents from '@hooks/analytics/use-analytic-events';
import Dialog from '@components/atoms/dialog/dialog';
import { NegotiationDialogStyles } from '@constants/negotiation';
import { NegotiationModal } from '@components/organisms/negotiation/negotiation-modal/negotiation-modal';
import { useNegotiation } from '@context/negotiation.context';
import { mapToMinimumPricingBreakdown } from '@maps/pricingBreakdown/pricingBreakdown';
import styles from './chat-header.module.scss';

const ChatMenu = dynamic(() => import('./chat-menu'), {
  ssr: false,
});

export interface ChatHeaderProps {
  addToCart: () => void;
  closeHandler?: () => void;
}

const ChatHeader: React.FC<ChatHeaderProps> = ({ addToCart, closeHandler }) => {
  const { t } = useTranslation();
  const { t: tA11y } = useTranslation('a11y');
  const { sendAnalyticEvent: sendChatAnalyticEvents } = useAnalyticEvents('chat');
  const { product, mode, channel, buyerLocation, buyerId, isSeller, isProductProhibited } = useChat();
  const isProductIdInCart = useContextSelector(CartContext, (v) => v.isProductIdInCart);
  const productsInCart = useContextSelector(CartContext, (v) => v.productsInCart);
  const checkoutUrl = useContextSelector(CartContext, (v) => v.checkoutUrl);
  const isSold = product.sold;
  const productUrl = product.path;
  const isShownRegularPrice =
    product?.regularPrice?.formatted && product?.regularPrice?.cents !== product?.price?.cents;
  const isInline = mode === 'inline';
  const receiverInfo = useReceiverInfo(product, channel);
  const [openNegotiationModal, setOpenNegotiationModal] = useState<boolean>(false);
  const [isCartPreviewOpened, setIsCartPreviewOpened] = useState<boolean>(false);
  const { initialData, negotiation } = useNegotiation();
  const priceBreakdown = mapToMinimumPricingBreakdown(product.pricingBreakdown);

  // fyi: on seller side, the buyer fees are sent from a different key (SU-897) - product.fees.hasCrossBorderFees
  const buyerLocationFromSellerSide = product?.pricingBreakdown?.buyerFees?.hasCrossBorderFees ? buyerLocation : '';

  const closePreviewBox = (): void => {
    setIsCartPreviewOpened(false);
  };

  const cartAction = (): void => {
    if (!isInline) {
      setIsCartPreviewOpened(!isCartPreviewOpened);
      return;
    }
    sendChatAnalyticEvents('bs_chat_view_bag');
    window.location.href = checkoutUrl;
  };

  const closeChat = (): void => {
    try {
      closeHandler();
    } catch (err) {
      logger.error(err.toString(), 'Error closing chat');
    }
  };

  const onGotoProductLink = () => {
    sendChatAnalyticEvents('product_click', {
      product_id: product.id,
      seller_id: product.seller?.id,
      product_unit_price_ati: ((product.price?.cents ?? 0) / 100).toString(),
      product_universe: product.universe?.name,
      product_category: product.category?.name,
      product_sub_category: product.subcategory?.name,
      product_brand: product.brand?.name,
      product_country: product.seller?.country,
    });
  };

  const canNegotiate = (): boolean => {
    return product.negotiable && (!!negotiation || !!initialData);
  };

  return (
    <div className={styles.chatHeader}>
      {/* Top row with the recipient and buttons */}
      <div
        className={clsx(
          styles.chatHeader__header,
          styles['chatHeader__header--notifications'],
          isInline && styles.chatHeader__header__inline,
        )}
      >
        {isInline ? (
          <h1 className={styles.chatHeader__recipient}>@{receiverInfo?.username}</h1>
        ) : (
          <span className={styles.chatHeader__recipient}>@{receiverInfo?.username}</span>
        )}
        {/* Close button */}
        <Button
          disableDefaultStyling
          className={styles.chatHeader__closeButton}
          onClick={closeChat}
          data-cy="chat-header-close-button"
        >
          <Icon
            className={styles.chatHeader__closeButtonIcon}
            name={isInline ? 'arrow-head-left' : 'cross-x'}
            ariaLabel={isInline ? tA11y('A11Y.ICON.ARROW_PREVIOUS') : t('MODAL.BUTTON.CLOSE')}
          />
        </Button>
      </div>
      {/* Second row on product pages with product information and action buttons */}
      <div
        className={clsx(
          styles.chatHeader__productInformation,
          isInline && styles.chatHeader__productInformation_inline,
        )}
      >
        {/* Product information */}
        <div className={styles.chatHeader__productInformationContainer}>
          {/* Product image */}
          <ConditionalWrapper
            condition={isInline}
            wrap={(children) => {
              return (
                <a
                  href={productUrl}
                  onClick={onGotoProductLink}
                >
                  {children}
                </a>
              );
            }}
          >
            <div className={styles.chatHeader__productInformationContainer}>
              <VCImage
                height={48}
                width={48}
                src={`/produit/${product.pictures[0].path}`}
                alt={product.name}
                data-cy="chat-header-product-image"
              />
            </div>
          </ConditionalWrapper>
          <div
            className={clsx(
              styles.chatHeader__productInformationTextContainer,
              isInline && styles.chatHeader__productInfoText__inline,
            )}
          >
            {/* Product brand */}
            <ConditionalWrapper
              condition={isInline}
              wrap={(children) => {
                return (
                  <a
                    href={productUrl}
                    onClick={onGotoProductLink}
                  >
                    {children}
                  </a>
                );
              }}
            >
              <p
                className={clsx(
                  styles.chatHeader__productInformationText,
                  styles['chatHeader__productInformationText--brand'],
                )}
                data-cy="chat-header-brand-name"
              >
                {product?.brand.name}
              </p>
              {/* Product condition */}
              <p
                className={clsx(
                  styles.chatHeader__productInformationText,
                  styles['chatHeader__productInformationText--condition'],
                )}
              >
                {product?.condition?.description}
              </p>
            </ConditionalWrapper>
            {/* Product price */}
            {!priceBreakdown && (
              <p
                className={clsx(
                  styles.chatHeader__productInformationText,
                  styles['chatHeader__productInformationText--price'],
                )}
              >
                {isInline && isShownRegularPrice && (
                  <span
                    className={styles.chatHeader__productInfo__regularPrice}
                    data-cy="chat-header-product-price"
                  >
                    {product?.regularPrice.formatted}
                  </span>
                )}
                <span
                  className={clsx(isInline && isShownRegularPrice && styles.chatHeader__productInfo__formattedPrice)}
                  data-cy="chat-header-product-price"
                >
                  {product?.price.formatted}
                </span>
              </p>
            )}
            {priceBreakdown && !isSeller && (
              <p
                className={clsx(
                  styles.chatHeader__productInformationText,
                  styles['chatHeader__productInformationText--price'],
                )}
                data-cy="chat-header-product-price"
              >
                {product.price.formatted}
                {/* Buyer fees */}
                <span className={styles.chatHeader__productInformationText__icon}>
                  <BuyerFeesModal
                    breakdown={priceBreakdown}
                    productLocation={product.seller.country}
                    trackingContext="bs_chat"
                    productId={product.id}
                  >
                    <Icon
                      name="infobox-default"
                      data-cy="product_price_info_icon"
                      ariaLabel={tA11y('A11Y.ICON.INFO')}
                    />
                  </BuyerFeesModal>
                </span>
              </p>
            )}
            {priceBreakdown && isSeller && (
              <>
                <p
                  className={clsx(
                    styles.chatHeader__productInformationText,
                    styles['chatHeader__productInformationText--price'],
                  )}
                >
                  {priceBreakdown.sellerPrice?.formatted}
                  <span className={clsx(styles.chatHeader__productInformationText__details)}>
                    ({t('CHAT.HEADER.PRICE_EARNING')}
                    {t(' ')}
                    {priceBreakdown.sellerEarning?.formatted})
                  </span>
                </p>
                {/* Buyer fees */}
                <p
                  className={clsx(
                    styles.chatHeader__productInformationText,
                    styles['chatHeader__productInformationText--buyerfee'],
                  )}
                >
                  {t('BUYERFEE.DISCLAIMER.EXCLUDED.SELLER')}
                  <span className={styles.chatHeader__productInformationText__icon}>
                    <BuyerFeesModal
                      breakdown={priceBreakdown}
                      isSeller={true}
                      buyerLocation={buyerLocationFromSellerSide}
                      trackingContext="bs_chat"
                      productId={product.id}
                    >
                      <Icon
                        name="infobox-default"
                        data-cy="product_price_info_icon"
                        ariaLabel={tA11y('A11Y.ICON.INFO')}
                      />
                    </BuyerFeesModal>
                  </span>
                </p>
              </>
            )}
            {isSold && <p className={clsx(styles.chatHeader__productInfo__sold)}>{t('PRODUCT_SNIPPET.SOLD')}</p>}
          </div>
        </div>
        {isInline && (
          <div className={clsx(styles.chatMenuContainer__lg__down)}>
            <ChatMenu product={product} />
          </div>
        )}

        {/* Action buttons */}
        <div className={clsx(styles.chatHeader__productInformationButtonsContainer, isInline && styles.button__inline)}>
          {!isSold && (
            <>
              {/* Show 'Make an offer' and 'Buy now' if product is not in cart */}
              {!isProductIdInCart(productsInCart, product.id) && (
                <>
                  {/* Make an offer button */}
                  <Button
                    disabled={!canNegotiate()}
                    loaded={!!negotiation || !!initialData}
                    variant="secondary"
                    size="small"
                    withLoader={canNegotiate()}
                    data-cy="chat_header_make_offer_cta"
                    onClick={() => {
                      if (negotiation && !negotiation.isClosed) {
                        sendChatAnalyticEvents('bs_chat_see_offer');
                      } else {
                        sendChatAnalyticEvents('bs_chat_make_offer');
                      }
                      setOpenNegotiationModal(true);
                    }}
                  >
                    {t(
                      negotiation && !negotiation.isClosed ? 'CHAT.HEADER.CTA.SEE_OFFERS' : 'CHAT.HEADER.CTA.NEGOCIATE',
                    )}
                  </Button>
                  {/* Buy now button */}
                  {!isSeller && !isProductProhibited && (
                    <Button
                      variant="primary"
                      size="small"
                      data-cy="chat_header_buy_cta"
                      onClick={() => {
                        sendChatAnalyticEvents('bs_chat_buy_now');
                        addToCart();
                      }}
                    >
                      {t('CHAT.HEADER.CTA.BUY_NOW')}
                    </Button>
                  )}
                </>
              )}
              {/* Show 'View your bag' if product is in the cart */}
              {isProductIdInCart(productsInCart, product.id) && (
                <>
                  {/* View cart button */}
                  <Button
                    variant={isInline ? 'primary' : 'outline'}
                    size="small"
                    onClick={cartAction}
                  >
                    {t('CHAT.HEADER.CTA.VIEW_BAG')}
                  </Button>
                  {/* Opens a popup with a list of items in the cart */}
                  <PreviewBox
                    isActive={isCartPreviewOpened}
                    title={t('HEADER.NAVIGATION.CART_TITLE')}
                    closePreviewBox={closePreviewBox}
                  >
                    <FloatingCart />
                  </PreviewBox>
                </>
              )}
            </>
          )}
          {isInline && (
            <div className={clsx(styles.chatMenuContainer)}>
              <ChatMenu product={product} />
            </div>
          )}
        </div>
      </div>
      <Dialog
        closeDialog={() => {
          setOpenNegotiationModal(false);
        }}
        key={`openNegotiationModal${openNegotiationModal}`}
        isOpen={openNegotiationModal}
        hasCloseIcon
        styles={{
          content: NegotiationDialogStyles,
        }}
      >
        {(modalDialogTitleId: string) => (
          <NegotiationModal
            trackingFrom="mmao_ongoing_banner"
            buyerId={isSeller ? buyerId : null}
            modalDialogTitleId={modalDialogTitleId}
          />
        )}
      </Dialog>
    </div>
  );
};

export default ChatHeader;
